import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { ArrowRight, Loader2, Scale } from "lucide-react";
import { api } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog } from "@/components/ui/dialog";
import { formatMilesFromKm } from "@/lib/units";

interface RebalanceSuggestion {
    jobId: string;
    jobTitle: string;
    fromTechId: string;
    fromTechName: string;
    toTechId: string;
    toTechName: string;
    minutes: number;
    reason: string;
    distanceKm: number | null;
}

interface RebalanceResponse {
    date: string;
    capacityMinutes: number;
    techs: Array<{
        id: string;
        name: string;
        bookedMinutes: number;
        stops: number;
    }>;
    suggestions: RebalanceSuggestion[];
}

interface RebalanceDialogProps {
    open: boolean;
    onClose: () => void;
    /** Date to rebalance — passes through to POST /api/dispatch/rebalance. */
    date: string;
    response: RebalanceResponse | undefined;
    isLoading: boolean;
}

/**
 * Modal that lists the heuristic rebalance suggestions returned from the server
 * and lets the dispatcher apply them individually or all at once. Each "apply"
 * is a PATCH on the underlying job (assignedTo only — start/end stay put so
 * the dispatcher can fine-tune in the swimlane afterward).
 */
export function RebalanceDialog({
    open,
    onClose,
    date,
    response,
    isLoading,
}: RebalanceDialogProps) {
    const qc = useQueryClient();
    const [appliedJobIds, setAppliedJobIds] = useState<Set<string>>(new Set());

    const applyOneMutation = useMutation({
        mutationFn: async (s: RebalanceSuggestion) => {
            await api.patch(`/api/jobs/${s.jobId}`, { assignedTo: s.toTechId });
            return s.jobId;
        },
        onSuccess: (jobId) => {
            setAppliedJobIds((prev) => new Set(prev).add(jobId));
            qc.invalidateQueries({ queryKey: ["dispatch"] });
            qc.invalidateQueries({ queryKey: ["dispatch-tech-routes"] });
        },
    });

    const applyAllMutation = useMutation({
        mutationFn: async (suggestions: RebalanceSuggestion[]) => {
            const applied: string[] = [];
            for (const s of suggestions) {
                if (appliedJobIds.has(s.jobId)) continue;
                try {
                    await api.patch(`/api/jobs/${s.jobId}`, { assignedTo: s.toTechId });
                    applied.push(s.jobId);
                } catch {
                    // continue applying others on best-effort
                }
            }
            return applied;
        },
        onSuccess: (applied) => {
            setAppliedJobIds((prev) => {
                const next = new Set(prev);
                for (const id of applied) next.add(id);
                return next;
            });
            qc.invalidateQueries({ queryKey: ["dispatch"] });
            qc.invalidateQueries({ queryKey: ["dispatch-tech-routes"] });
        },
    });

    const remaining = useMemo(
        () => response?.suggestions.filter((s) => !appliedJobIds.has(s.jobId)) ?? [],
        [response, appliedJobIds],
    );

    return (
        <Dialog
            open={open}
            onClose={() => {
                setAppliedJobIds(new Set());
                onClose();
            }}
            title={`Suggested moves for ${date}`}
        >
            <div className="space-y-3">
                <p className="text-xs text-foreground/80">
                    Light heuristic: techs over 8h capacity get their latest stops moved to nearby
                    techs running under 60% capacity. Reassigns only — keep start/end as-is, tune
                    on the swimlane after.
                </p>

                {isLoading ? (
                    <div className="flex h-24 items-center justify-center">
                        <Loader2 className="h-5 w-5 animate-spin text-foreground/80" />
                    </div>
                ) : !response ? (
                    <p className="py-6 text-center text-sm text-foreground/80">
                        No data loaded yet.
                    </p>
                ) : response.suggestions.length === 0 ? (
                    <div className="rounded-md border border-emerald-300 bg-emerald-50 p-4 text-sm text-emerald-900 dark:border-emerald-800/60 dark:bg-emerald-950/40 dark:text-emerald-100">
                        Everyone's within capacity. No moves needed.
                    </div>
                ) : (
                    <>
                        <div className="space-y-2">
                            {response.suggestions.map((s) => {
                                const applied = appliedJobIds.has(s.jobId);
                                return (
                                    <div
                                        key={s.jobId}
                                        className="rounded-md border bg-card p-3 text-sm"
                                    >
                                        <div className="flex items-start justify-between gap-3">
                                            <div className="min-w-0 flex-1">
                                                <div className="truncate font-medium">{s.jobTitle}</div>
                                                <div className="mt-1 flex items-center gap-2 text-xs text-foreground/80">
                                                    <span className="truncate">{s.fromTechName}</span>
                                                    <ArrowRight className="h-3 w-3 shrink-0" />
                                                    <span className="truncate">{s.toTechName}</span>
                                                    <Badge
                                                        variant="secondary"
                                                        className="ml-1 text-[10px]"
                                                    >
                                                        {s.minutes}m
                                                    </Badge>
                                                    {s.distanceKm != null && (
                                                        <Badge
                                                            variant="outline"
                                                            className="text-[10px]"
                                                        >
                                                            ~{formatMilesFromKm(s.distanceKm)}
                                                        </Badge>
                                                    )}
                                                </div>
                                                <p className="mt-1 text-[11px] italic text-foreground/80">
                                                    {s.reason}
                                                </p>
                                            </div>
                                            <Button
                                                size="sm"
                                                variant={applied ? "outline" : "default"}
                                                disabled={applied || applyOneMutation.isPending}
                                                onClick={() => applyOneMutation.mutate(s)}
                                            >
                                                {applied ? "Moved" : "Apply"}
                                            </Button>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        <div className="flex items-center justify-between border-t pt-3">
                            <div className="text-xs text-foreground/80">
                                {appliedJobIds.size} of {response.suggestions.length} applied
                            </div>
                            <div className="flex gap-2">
                                <Button variant="outline" size="sm" onClick={onClose}>
                                    Close
                                </Button>
                                <Button
                                    size="sm"
                                    onClick={() => applyAllMutation.mutate(remaining)}
                                    disabled={remaining.length === 0 || applyAllMutation.isPending}
                                >
                                    {applyAllMutation.isPending && (
                                        <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                                    )}
                                    <Scale className="mr-1 h-3.5 w-3.5" />
                                    Apply remaining ({remaining.length})
                                </Button>
                            </div>
                        </div>
                    </>
                )}
            </div>
        </Dialog>
    );
}

export type { RebalanceSuggestion, RebalanceResponse };
